import { useState, useEffect } from 'react';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Zap, Landmark, CheckCircle, AlertCircle, PieChart,
  Trash2, Link2, Unlink, FileSpreadsheet, Tag, BookOpen, Pencil
} from 'lucide-react';
import axios from 'axios';
import AccountingImportModal from './AccountingImportModal';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingBankRecon({ reconciliationSummary, bankTransactions, transactions, loadData }) {
  const [showImportModal, setShowImportModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [editTrans, setEditTrans] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', description: '', amount: '', transaction_type: '' });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token = getToken();
        const res = await axios.get(`${API}/api/accounting/chart-of-accounts`, { headers: { Authorization: `Bearer ${token}` } });
        const all = Array.isArray(res.data) ? res.data : res.data.accounts || [];
        setAccounts(all.filter(a => a.is_active));
      } catch {}
    };
    fetchAccounts();
  }, []);

  // Reset selection when data reloads
  useEffect(() => { setSelectedIds([]); }, [bankTransactions]);

  const allSelected = bankTransactions.length > 0 && selectedIds.length === bankTransactions.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : bankTransactions.map(t => t.id));
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    const msg = selectedIds.length === bankTransactions.length
      ? `Delete ALL ${bankTransactions.length} bank transactions?`
      : `Delete ${selectedIds.length} selected transaction(s)?`;
    if (!window.confirm(msg)) return;

    setDeleting(true);
    const token = getToken();
    try {
      const payload = selectedIds.length === bankTransactions.length
        ? { delete_all: true }
        : { ids: selectedIds };
      const res = await axios.post(`${API}/api/accounting/bank-transactions/bulk-delete`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setSelectedIds([]);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const autoMatchTransactions = async () => {
    const token = getToken();
    try {
      const response = await axios.post(`${API}/api/accounting/bank-transactions/auto-match`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(response.data.message);
      loadData();
    } catch (err) {
      toast.error('Auto-match failed');
    }
  };

  const matchTransaction = async (bankTransId, accountingTransId) => {
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/bank-transactions/${bankTransId}/match/${accountingTransId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transactions matched');
      loadData();
    } catch (err) {
      toast.error('Match failed');
    }
  };

  const unmatchTransaction = async (bankTransId) => {
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/bank-transactions/${bankTransId}/unmatch`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction unmatched');
      loadData();
    } catch (err) {
      toast.error('Unmatch failed');
    }
  };

  const deleteBankTransaction = async (id) => {
    if (!window.confirm('Delete this bank transaction?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/bank-transactions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openEdit = (trans) => {
    setEditTrans(trans);
    setEditForm({
      date: trans.date || '',
      description: trans.description || '',
      amount: trans.amount || '',
      transaction_type: trans.transaction_type || 'debit',
    });
  };

  const saveEdit = async () => {
    if (!editTrans) return;
    const token = getToken();
    try {
      await axios.put(`${API}/api/accounting/bank-transactions/${editTrans.id}`, {
        date: editForm.date,
        description: editForm.description,
        amount: parseFloat(editForm.amount) || 0,
        transaction_type: editForm.transaction_type,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction updated');
      setEditTrans(null);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const categorizeTransaction = async (transId, accountId) => {
    const token = getToken();
    try {
      const res = await axios.post(
        `${API}/api/accounting/bank-transactions/${transId}/categorize`,
        { account_id: accountId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Categorization failed');
    }
  };

  const expenseAccounts = accounts.filter(a => a.account_type === 'expense');
  const incomeAccounts = accounts.filter(a => a.account_type === 'income');

  return (
    <>
      {/* Summary Cards */}
      {reconciliationSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><AlertCircle className="w-6 h-6 text-amber-600" /></div><div><p className="text-xs text-slate-500 uppercase">Unmatched</p><p className="text-xl font-bold text-amber-600">{reconciliationSummary.counts?.unmatched || 0}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-emerald-600" /></div><div><p className="text-xs text-slate-500 uppercase">Matched</p><p className="text-xl font-bold text-emerald-600">{reconciliationSummary.counts?.matched || 0}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><Tag className="w-6 h-6 text-blue-600" /></div><div><p className="text-xs text-slate-500 uppercase">Categorized</p><p className="text-xl font-bold text-blue-600">{reconciliationSummary.counts?.categorized || 0}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center"><Landmark className="w-6 h-6 text-slate-600" /></div><div><p className="text-xs text-slate-500 uppercase">Total</p><p className="text-xl font-bold text-slate-600">{reconciliationSummary.counts?.total || 0}</p></div></div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center"><PieChart className="w-6 h-6 text-purple-600" /></div><div><p className="text-xs text-slate-500 uppercase">Match Rate</p><p className="text-xl font-bold text-purple-600">{reconciliationSummary.match_percentage || 0}%</p></div></div></CardContent></Card>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap items-center">
        <Button onClick={() => setShowImportModal(true)} data-testid="import-bank-btn">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Import Bank Statement
        </Button>
        <Button variant="outline" onClick={autoMatchTransactions} data-testid="auto-match-btn">
          <Zap className="w-4 h-4 mr-2" /> Auto-Match Transactions
        </Button>
        {selectedIds.length > 0 && (
          <Button variant="destructive" onClick={bulkDelete} disabled={deleting} data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting ? 'Deleting...' : `Delete ${selectedIds.length} Selected`}
          </Button>
        )}
      </div>

      {/* Bank Transactions List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Landmark className="w-5 h-5" /> Bank Transactions
          </CardTitle>
          <CardDescription>Import bank statements and match with accounting transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {bankTransactions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Landmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No bank transactions imported yet</p>
              <p className="text-sm">Import your bank statement to start reconciliation</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bankTransactions.map((trans) => {
                    const isNew = trans.created_at && (Date.now() - new Date(trans.created_at).getTime()) < 10 * 60 * 1000;
                    return (
                    <TableRow key={trans.id} className={
                      selectedIds.includes(trans.id) ? 'bg-red-50/50' :
                      isNew && trans.status === 'unmatched' ? 'bg-yellow-50 border-l-2 border-l-yellow-400' :
                      trans.status === 'matched' ? 'bg-emerald-50' :
                      trans.status === 'categorized' ? 'bg-blue-50' : ''
                    }>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(trans.id)}
                          onCheckedChange={() => toggleOne(trans.id)}
                          data-testid={`select-${trans.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <span className="flex items-center gap-1.5">
                          {trans.date}
                          {isNew && <Badge className="bg-yellow-400 text-yellow-900 text-[10px] px-1 py-0 h-4">NEW</Badge>}
                        </span>
                      </TableCell>
                      <TableCell>{trans.description}</TableCell>
                      <TableCell className={`text-right font-mono ${trans.transaction_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {trans.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(trans.amount)}
                      </TableCell>
                      <TableCell>
                        {trans.status === 'categorized' ? (
                          <Badge variant="outline" className="text-blue-700 border-blue-300 gap-1">
                            <BookOpen className="w-3 h-3" />
                            {trans.categorized_account_name}
                          </Badge>
                        ) : trans.status === 'unmatched' ? (
                          <Select onValueChange={(accId) => categorizeTransaction(trans.id, accId)} data-testid={`categorize-${trans.id}`}>
                            <SelectTrigger className="h-8 text-xs w-[180px]">
                              <SelectValue placeholder="Assign account..." />
                            </SelectTrigger>
                            <SelectContent>
                              {trans.transaction_type === 'debit' ? (
                                <>
                                  <SelectItem value="_header_expense" disabled className="font-bold text-xs text-slate-500">Expense Accounts</SelectItem>
                                  {expenseAccounts.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.account_number} {a.name}</SelectItem>
                                  ))}
                                </>
                              ) : (
                                <>
                                  <SelectItem value="_header_income" disabled className="font-bold text-xs text-slate-500">Income Accounts</SelectItem>
                                  {incomeAccounts.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.account_number} {a.name}</SelectItem>
                                  ))}
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={
                          trans.status === 'matched' ? 'bg-emerald-100 text-emerald-800' :
                          trans.status === 'categorized' ? 'bg-blue-100 text-blue-800' :
                          trans.status === 'reconciled' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {trans.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {trans.status === 'matched' ? (
                            <Button variant="ghost" size="sm" onClick={() => unmatchTransaction(trans.id)} title="Unmatch">
                              <Unlink className="w-4 h-4 text-amber-600" />
                            </Button>
                          ) : trans.status === 'unmatched' ? (
                            <Select onValueChange={(accountingId) => matchTransaction(trans.id, accountingId)}>
                              <SelectTrigger className="w-8 h-8 p-0" title="Manual match">
                                <Link2 className="w-4 h-4 text-blue-600" />
                              </SelectTrigger>
                              <SelectContent>
                                {transactions.filter(t => !t.reconciled).slice(0, 20).map(t => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.date} - {t.description?.substring(0, 20)} - {formatCurrency(t.debit || t.credit)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : null}
                          <Button variant="ghost" size="sm" onClick={() => openEdit(trans)} title="Edit" data-testid={`edit-${trans.id}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteBankTransaction(trans.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Bank Reconciliation Guide</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>- <strong>Import:</strong> Upload your bank statement (Excel, CSV, or PDF) and map columns</li>
            <li>- <strong>Categorize:</strong> Assign unmatched transactions to a Chart of Account -- this auto-creates expense/income entries</li>
            <li>- <strong>Auto-Match:</strong> Automatically matches transactions by amount and date (3 days)</li>
            <li>- <strong>Manual Match:</strong> Click the link icon to manually match with accounting entries</li>
            <li>- <strong>Bulk Delete:</strong> Use checkboxes to select multiple transactions, then click Delete Selected</li>
          </ul>
        </CardContent>
      </Card>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editTrans} onOpenChange={(open) => { if (!open) setEditTrans(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Bank Transaction</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Date</Label>
              <Input type="date" value={editForm.date} onChange={e => setEditForm(p => ({ ...p, date: e.target.value }))} data-testid="edit-date" />
            </div>
            <div className="grid gap-1.5">
              <Label>Description</Label>
              <Input value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} data-testid="edit-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Amount</Label>
                <Input type="number" step="0.01" min="0" value={editForm.amount} onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))} data-testid="edit-amount" />
              </div>
              <div className="grid gap-1.5">
                <Label>Type</Label>
                <Select value={editForm.transaction_type} onValueChange={v => setEditForm(p => ({ ...p, transaction_type: v }))}>
                  <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit (Withdrawal)</SelectItem>
                    <SelectItem value="credit">Credit (Deposit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {editTrans?.status === 'categorized' && editForm.transaction_type !== editTrans?.transaction_type && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Changing the type will un-categorize this transaction and reverse its accounting entries.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTrans(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="edit-save-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel/CSV/PDF Import Modal */}
      <AccountingImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importType="bank_transaction"
        onImportComplete={loadData}
      />
    </>
  );
}
